let uid = 0;
function nextId() {
    uid += 1;
    return `cs-opt-${uid}`;
}

function normalize(str) {
    return (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
}

function wrapSelect(select) {
    if (select.dataset.csInit) return;
    select.dataset.csInit = '1';

    const wrapper = document.createElement('div');
    wrapper.className = 'cs';
    // The select carries its own layout (max-width, flex sizing) via inline
    // style in a few forms (e.g. the V/E nationality select capped to
    // 100px). Once hidden inside `.cs__native`, that style no longer affects
    // anything visible, so it must move to the wrapper — the new visible
    // flex item in the select's place.
    if (select.hasAttribute('style')) {
        wrapper.setAttribute('style', select.getAttribute('style'));
    }
    select.parentNode.insertBefore(wrapper, select);
    wrapper.appendChild(select);

    select.classList.add('cs__native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'cs__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    const triggerText = document.createElement('span');
    triggerText.className = 'cs__trigger-text';
    trigger.appendChild(triggerText);
    const triggerArrow = document.createElement('span');
    triggerArrow.className = 'cs__trigger-arrow';
    triggerArrow.setAttribute('aria-hidden', 'true');
    trigger.appendChild(triggerArrow);
    wrapper.appendChild(trigger);

    const panel = document.createElement('div');
    panel.className = 'cs__panel';
    panel.hidden = true;

    const searchWrap = document.createElement('div');
    searchWrap.className = 'cs__search-wrap';
    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.className = 'cs__search';
    searchInput.placeholder = 'Buscar...';
    searchInput.setAttribute('aria-label', 'Buscar');
    searchInput.setAttribute('role', 'combobox');
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.setAttribute('aria-controls', `${trigger.id || nextId()}-list`);
    searchWrap.appendChild(searchInput);
    panel.appendChild(searchWrap);

    const list = document.createElement('ul');
    list.className = 'cs__options';
    list.id = searchInput.getAttribute('aria-controls');
    list.setAttribute('role', 'listbox');
    panel.appendChild(list);

    wrapper.appendChild(panel);

    const lis = [];
    Array.from(select.options).forEach((option) => {
        const li = document.createElement('li');
        li.id = nextId();
        li.className = 'cs__option';
        li.setAttribute('role', 'option');
        li.dataset.value = option.value;
        li.textContent = option.textContent;
        Object.assign(li.dataset, option.dataset);
        if (option.disabled) li.classList.add('cs__option--disabled');
        list.appendChild(li);
        lis.push(li);
    });

    let currentParentValue = null;
    let currentSearch = '';
    let highlightedLi = null;
    let dependents = [];

    function visibleOptions() {
        return lis.filter((li) => li.style.display !== 'none' && !li.classList.contains('cs__option--disabled'));
    }

    function recomputeVisibility() {
        const q = normalize(currentSearch);
        lis.forEach((li, idx) => {
            const isPlaceholder = li.dataset.value === '';
            const matchesParent = isPlaceholder || currentParentValue === null || !('parent' in li.dataset) || li.dataset.parent === currentParentValue;
            const matchesSearch = q === '' || normalize(li.textContent).indexOf(q) !== -1;
            const visible = matchesParent && matchesSearch;
            li.style.display = visible ? '' : 'none';
            select.options[idx].hidden = !matchesParent;
        });
    }

    function updateTriggerText() {
        const opt = select.options[select.selectedIndex];
        triggerText.textContent = opt ? opt.textContent : '';
        triggerText.classList.toggle('cs__trigger-text--placeholder', !opt || opt.value === '');
    }

    function updateSelectedLi() {
        lis.forEach((li) => li.classList.toggle('cs__option--selected', li.dataset.value === select.value));
    }

    function updateValidationState() {
        if (select.dataset.required === undefined) return;
        const hasValue = select.value !== '';
        // Deliberately namespaced (not the bare .required/.success used by
        // forms.js's validateForm()): that helper wires focus/blur listeners
        // to *any* element matching ".required" and checks `element.value`,
        // which for a <button> is always "" — its blur handler would
        // permanently re-flag this trigger as invalid the first time focus
        // leaves it, regardless of the underlying select's real value.
        trigger.classList.toggle('cs__trigger--success', hasValue);
        trigger.classList.toggle('cs__trigger--required', !hasValue);
    }

    function setHighlight(li) {
        if (highlightedLi) highlightedLi.classList.remove('cs__option--active');
        highlightedLi = li;
        if (li) {
            li.classList.add('cs__option--active');
            li.scrollIntoView({ block: 'nearest' });
            searchInput.setAttribute('aria-activedescendant', li.id);
        } else {
            searchInput.removeAttribute('aria-activedescendant');
        }
    }

    function isOpen() {
        return !panel.hidden;
    }

    function open() {
        if (select.disabled || isOpen()) return;
        currentSearch = '';
        searchInput.value = '';
        recomputeVisibility();
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        searchInput.setAttribute('aria-expanded', 'true');
        wrapper.classList.add('cs--open');
        const selected = lis.find((li) => li.dataset.value === select.value && li.style.display !== 'none');
        setHighlight(selected || visibleOptions()[0] || null);
        searchInput.focus();
    }

    function close({ returnFocus = false } = {}) {
        if (!isOpen()) return;
        panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        searchInput.setAttribute('aria-expanded', 'false');
        wrapper.classList.remove('cs--open');
        setHighlight(null);
        if (returnFocus) trigger.focus();
    }

    function selectLi(li) {
        if (!li || li.classList.contains('cs__option--disabled')) return;
        if (select.value !== li.dataset.value) {
            select.value = li.dataset.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            select.dispatchEvent(new Event('input', { bubbles: true }));
        }
        close({ returnFocus: true });
    }

    function moveHighlight(delta) {
        const opts = visibleOptions();
        if (!opts.length) return;
        const idx = highlightedLi ? opts.indexOf(highlightedLi) : -1;
        let next = idx + delta;
        if (next < 0) next = 0;
        if (next > opts.length - 1) next = opts.length - 1;
        setHighlight(opts[next]);
    }

    trigger.addEventListener('click', () => {
        if (isOpen()) {
            close({ returnFocus: true });
        } else {
            open();
        }
    });

    trigger.addEventListener('keydown', (e) => {
        if (select.disabled) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            open();
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            // Let the native button click-activation (fired on keyup/keydown by the browser)
            // drive open/close via the click listener below — avoids a double-toggle.
            return;
        }
        if (e.key === 'Escape') {
            close({ returnFocus: true });
            return;
        }
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            open();
            searchInput.value = e.key;
            currentSearch = e.key;
            recomputeVisibility();
            setHighlight(visibleOptions()[0] || null);
        }
    });

    searchInput.addEventListener('input', () => {
        currentSearch = searchInput.value;
        recomputeVisibility();
        setHighlight(visibleOptions()[0] || null);
    });

    searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            moveHighlight(1);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            moveHighlight(-1);
        } else if (e.key === 'Home') {
            e.preventDefault();
            setHighlight(visibleOptions()[0] || null);
        } else if (e.key === 'End') {
            e.preventDefault();
            const opts = visibleOptions();
            setHighlight(opts[opts.length - 1] || null);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            selectLi(highlightedLi);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            close({ returnFocus: true });
        } else if (e.key === 'Tab') {
            close({ returnFocus: false });
        }
    });

    list.addEventListener('click', (e) => {
        const li = e.target.closest('.cs__option');
        if (li) selectLi(li);
    });

    list.addEventListener('mousemove', (e) => {
        const li = e.target.closest('.cs__option');
        if (li && li !== highlightedLi && li.style.display !== 'none') setHighlight(li);
    });

    document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target)) close({ returnFocus: false });
    });

    select.addEventListener('change', () => {
        updateTriggerText();
        updateSelectedLi();
        updateValidationState();
        dependents.forEach((dep) => dep.notifyParentChange(select.value));
    });

    const observer = new MutationObserver(() => {
        trigger.disabled = select.disabled;
        trigger.classList.toggle('cs__trigger--disabled', select.disabled);
        if (select.disabled) close({ returnFocus: false });
        // Mirror validation classes set directly on the (hidden) select by
        // existing code — e.g. forms.js's submit-time checkFormIsValid()
        // adding .required to flag an empty field — onto the visible trigger.
        trigger.classList.toggle('cs__trigger--required', select.classList.contains('required'));
        trigger.classList.toggle('cs__trigger--success', select.classList.contains('success'));
    });
    observer.observe(select, { attributes: true, attributeFilter: ['disabled', 'class'] });
    trigger.disabled = select.disabled;
    trigger.classList.toggle('cs__trigger--disabled', select.disabled);

    updateTriggerText();
    updateSelectedLi();
    updateValidationState();

    return {
        select,
        notifyParentChange(parentValue) {
            currentParentValue = parentValue;
            recomputeVisibility();
            const stillValid = lis.some((li) => li.dataset.value === select.value && li.style.display !== 'none');
            if (!stillValid && select.value !== '') {
                select.value = '';
                select.dispatchEvent(new Event('change', { bubbles: true }));
            }
        },
        registerDependent(dep) {
            dependents.push(dep);
        },
    };
}

export function initAllCustomSelects(root = document) {
    const instances = new Map();
    const selects = Array.from(root.querySelectorAll('select:not([data-no-cs])'));

    selects.forEach((select) => {
        instances.set(select, wrapSelect(select));
    });

    selects.forEach((select) => {
        const dependsOnId = select.dataset.csDependsOn;
        if (!dependsOnId) return;
        const parentSelect = document.getElementById(dependsOnId);
        if (!parentSelect) return;
        const parentInstance = instances.get(parentSelect);
        const childInstance = instances.get(select);
        if (!parentInstance || !childInstance) return;
        parentInstance.registerDependent(childInstance);
        childInstance.notifyParentChange(parentSelect.value);
    });

    return instances;
}
